// Parcel splitting — mirror of App\Services\Shipping\ParcelSplitter.
// Keep both implementations aligned: this one drives the over-limit warning
// and the split preview on the order forms; the server re-cuts the cart on
// submit and refuses anything that doesn't match.
//
// India Post will not book a single Speed Post consignment above 100 units or
// 20 kg. A cart past either cap ships as several parcels, and in this OMS one
// parcel == one order (one label, one invoice, one tracking id).
//
// The cut is a GREEDY FILL in cart order: pack the current parcel until the
// next unit would breach a cap, close it, start the next. Fewest parcels =
// fewest bookings = lowest shipping cost, and the result is deterministic, so
// what staff previewed is what the server creates.
//
// A LINE may be cut across parcels: 150 bottles of one SKU becomes 100 + 50.
// Line money is re-derived per parcel from `price × quantity`, so parcel
// totals sum back to the cart total exactly.

// Fallbacks used until GET /api/shipping/parcel-limits answers. Same values as
// config/shipping.php → parcel_limits; the server response always wins.
export const DEFAULT_PARCEL_LIMITS = {
  ip_speed: { max_units: 100, max_weight_grams: 20000 }
}

const round2 = (n) => Number(Number(n || 0).toFixed(2))

export function limitsForMethod (limitsMap, shippingMethod) {
  const map = limitsMap && typeof limitsMap === 'object' ? limitsMap : DEFAULT_PARCEL_LIMITS
  const found = map[String(shippingMethod || '')] || {}
  return {
    maxUnits: Math.max(0, Number(found.max_units || 0)),
    maxWeightGrams: Math.max(0, Number(found.max_weight_grams || 0))
  }
}

// [Σ quantity, Σ quantity × shipping_weight] for a cart.
export function measureCart (lines) {
  return (Array.isArray(lines) ? lines : []).reduce((acc, line) => {
    const qty = Number(line.quantity || 0)
    if (qty <= 0) return acc
    acc.units += qty
    acc.weightGrams += qty * Number(line.shipping_weight || 0)
    return acc
  }, { units: 0, weightGrams: 0 })
}

export function fitsInOneParcel (limits, lines) {
  const { maxUnits, maxWeightGrams } = limits
  const { units, weightGrams } = measureCart(lines)
  return (!maxUnits || units <= maxUnits) && (!maxWeightGrams || weightGrams <= maxWeightGrams)
}

// Units that can never be packed legally — a single unit heavier than the
// whole weight cap. Non-empty means the cart cannot ship by this method at
// all, which is an error to surface, not a split to perform.
export function oversizedUnits (limits, lines) {
  if (!limits.maxWeightGrams) return []
  return (Array.isArray(lines) ? lines : []).filter(
    (l) => Number(l.quantity || 0) > 0 && Number(l.shipping_weight || 0) > limits.maxWeightGrams
  )
}

// How many more units of a given unit-weight the open parcel can accept.
function capacityFor (limits, parcel, unitGrams) {
  const byUnits = limits.maxUnits > 0 ? limits.maxUnits - parcel.units : Infinity
  const byWeight = limits.maxWeightGrams > 0 && unitGrams > 0
    ? Math.floor((limits.maxWeightGrams - parcel.weightGrams) / unitGrams)
    : Infinity
  return Math.max(0, Math.min(byUnits, byWeight))
}

const emptyParcel = () => ({ lines: [], units: 0, weightGrams: 0, total: 0 })

// Cut `lines` into parcels. Each input line carries at minimum
// { quantity, price, shipping_weight } plus whatever identifying keys the
// caller needs echoed back (item_id / sku / name / shortcode); every key is
// preserved, only quantity / total / weight_grams are rewritten.
//
// Returns [{ lines, units, weightGrams, total }, ...].
export function splitCartIntoParcels (limits, lines) {
  const parcels = []
  let current = emptyParcel()

  ;(Array.isArray(lines) ? lines : []).forEach((line) => {
    let remaining = Number(line.quantity || 0)
    const price = Number(line.price || 0)
    const unitGrams = Number(line.shipping_weight || 0)
    if (remaining <= 0) return

    while (remaining > 0) {
      let canTake = capacityFor(limits, current, unitGrams)

      // Nothing more fits — close the parcel and retry against a fresh one.
      // The `units > 0` guard is what stops an un-splittable unit (heavier
      // than the whole cap) from spinning: on an empty parcel we take it
      // anyway and let oversizedUnits() report the cart as unshippable.
      if (canTake <= 0) {
        if (current.units > 0) {
          parcels.push(current)
          current = emptyParcel()
          continue
        }
        canTake = 1
      }

      const take = Math.min(remaining, canTake)

      current.lines.push({
        ...line,
        quantity: take,
        total: round2(take * price),
        weight_grams: round2(take * unitGrams)
      })
      current.units += take
      current.weightGrams = round2(current.weightGrams + (take * unitGrams))
      current.total = round2(current.total + (take * price))

      remaining -= take
    }
  })

  if (current.units > 0) parcels.push(current)

  return parcels
}

// Apportion a whole-cart discount across parcels.
//
// This is the whole point of splitting server-side rather than asking staff to
// key two carts: the discount chart is resolved ONCE against the COMBINED cart,
// so a 150-bottle order still clears the 100+ rung even though neither parcel
// reaches it on its own. Each parcel then carries its share.
//
// `wholeCartBreakdown` is lineDiscountBreakdown(tiers, wholeCartLines) — the
// per-line amount the FULL cart earned. A parcel line's share is that line's
// amount scaled by the quantity this parcel took, so the shares sum back to
// the whole-cart discount. The last parcel absorbs the rounding remainder,
// which keeps Σ(parcel discounts) exactly equal to the cart discount.
export function apportionDiscount (parcels, wholeCartLines, wholeCartBreakdown, totalDiscount) {
  const byItem = {}
  ;(wholeCartLines || []).forEach((line, i) => {
    const key = String(line.item_id)
    if (!byItem[key]) byItem[key] = { quantity: 0, amount: 0 }
    byItem[key].quantity += Number(line.quantity || 0)
    byItem[key].amount += Number(wholeCartBreakdown?.[i]?.amount || 0)
  })

  const shares = parcels.map((parcel) => {
    const amount = parcel.lines.reduce((sum, line) => {
      const whole = byItem[String(line.item_id)]
      if (!whole || whole.quantity <= 0 || whole.amount <= 0) return sum
      return sum + ((whole.amount * Number(line.quantity || 0)) / whole.quantity)
    }, 0)
    return round2(amount)
  })

  // Push the rounding remainder onto the last parcel that earned something,
  // so the parcels always add up to the discount the cart actually qualified
  // for — never a rupee more (an over-discount isn't recoverable).
  const target = round2(totalDiscount)
  const drift = round2(target - shares.reduce((a, b) => a + b, 0))
  if (drift !== 0) {
    const last = shares.reduce((idx, val, i) => (val > 0 ? i : idx), -1)
    if (last >= 0) shares[last] = round2(shares[last] + drift)
  }

  return shares
}
